'use client'

import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import dynamic from 'next/dynamic'
import { useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  ConfirmationDialog,
} from '@/components/ui/Dialog'
import { X, Trash2, Loader2, ExternalLink, Check } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/Dropdown'
import { Spinner } from '@/components/ui/Loading'
import { showToast } from '@/components/ui/Toast'
import { Tooltip } from '@/components/ui/Tooltip'
import { FilterPills } from './FilterPills'
import { ImageGrid } from './ImageGrid'
import { SelectionTray } from './SelectionTray'
import { logger } from '@/lib/logger'
import { getImageUrl, MAX_IMAGE_SELECTION } from '@/lib'
import { analytics } from '@/lib/analytics'
import {
  processFileForUpload,
  validateMultipleImageFiles,
  MAX_FILES,
  MAX_FILE_SIZE,
} from '@/helpers/images'
import { getDirectUploadHostConcurrency } from '@/utils/env'
import { formatFailedForDisplay } from '@/utils/sanitize'
import { formatFileSize } from '@/utils/fileValidation'
import { buildMergedUploaderDisplayMap } from '@/app/(authenticated)/invitations/[invitationId]/gallery/galleryPageHelpers'
import { ApiError } from '@/services/shared/client'
import {
  useUserImagesInfinite,
  useUserGuestUploadsInfinite,
  useGuestImagesInfinite,
  useDeleteUserImages,
  useDeleteGuestImages,
  useToggleFavoriteImages,
  libraryKeys,
} from '@/services/library'
import { uploadGuestImages, uploadUserImages } from '@/services/library/api'
import { preloadHeicWorker, terminateHeicWorker } from '@/helpers/heicWorker'
import {
  startDirectUploadBatch,
  type DirectUploadBatchResult,
  type DirectUploadBatchTask,
  type UploadLifecycleStatus,
} from '@/services/library/uploadClient'
import {
  useCloudConnections,
  useStartExportAllGuestUploads,
  useExportJob,
  useDisconnectProvider,
  exportKeys,
  applyOptimisticCloudConnection,
  scheduleCloudConnectionsReconcile,
} from '@/services/export'
import {
  connectExportProviderInPopup,
  EXPORT_OAUTH_CANCELLED,
} from '@/lib/exportOAuthPopup'
import type {
  StoredImage,
  SelectedImage,
  ImageContext,
  ImageStatus,
  ImageSortOption,
  AspectRatio,
} from '@/types/image'
import {
  CONTEXT_LABELS,
  CONTEXT_ASPECT_RATIOS,
  CONTEXT_DIMENSIONS,
  CONTEXT_IMAGE_LIMITS,
} from '@/types/image'

/** Picker selection item — uses a unique instanceId to allow the same image to be selected multiple times */
export interface PickerSelection {
  instanceId: string
  imageId: string
  imageUrl: string
}

// Converts hyphenated/underscored orientation keys to readable labels
// e.g. "portrait-tall" → "Portrait Tall", "portrait_or_landscape" → "Portrait or landscape"
const formatOrientationLabel = (orientation: string): string => {
  if (orientation === 'portrait_or_landscape') return 'Portrait or landscape'
  return orientation
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const generateInstanceId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

// Extracts the failed-files array from a structured ApiError response
function getFailedFromApiError(
  err: unknown
): Array<{ filename: string; error: string }> | null {
  if (!(err instanceof ApiError) || !err.data || typeof err.data !== 'object')
    return null
  const d = (err.data as { data?: { failed?: unknown } }).data
  return Array.isArray(d?.failed)
    ? (d.failed as Array<{ filename: string; error: string }>)
    : null
}

// Lazy-loaded so the canvas/editor bundle is not downloaded until the user clicks Edit
const ImageEditorModal = dynamic(
  () => import('./ImageEditorModal').then((mod) => mod.ImageEditorModal),
  {
    loading: () => (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    ),
    ssr: false,
  }
)

interface ImageLibraryModalProps {
  isOpen: boolean
  onClose: () => void
  /** Determines aspect ratio hints and selection limits */
  context: ImageContext
  onSelect: (image: SelectedImage) => void
  onSelectMultiple?: (images: SelectedImage[]) => void
  invitationId: string | null | undefined
  isAuthenticated: boolean
  initialSelectedId?: string | null
  /** Pre-selected URLs matched against loaded images on first open */
  initialSelectedUrls?: string[]
  /** Overrides the context max, enabling multi-select when > 1 */
  maxImages?: number
  /** Auto-opens the export dialog on mount — used after OAuth redirect back to the page */
  autoOpenExport?: boolean
  orientation?:
    | 'portrait'
    | 'portrait-tall'
    | 'landscape'
    | 'landscape-wide'
    | 'square'
    | 'portrait_or_landscape'
  preferredAspectRatio?: string
  recommendedDimensions?: string
  /** When set, only these crop presets are offered in the editor */
  allowedAspectRatios?: AspectRatio[]
  contextLabel?: string
  /** Allows the same image to be added to the selection multiple times */
  allowDuplicates?: boolean
  /** Called instead of onSelectMultiple when allowDuplicates is true — preserves instanceIds */
  onSelectWithInstances?: (selections: PickerSelection[]) => void
}

/**
 * Main modal for browsing, uploading, filtering, and selecting images.
 * Supports single-select, multi-select, and duplicate-picker modes.
 * Opens ImageEditorModal lazily when the user clicks Edit on a card.
 */

// ── Grid size config (JS-driven, immune to Tailwind purging) ──────────────
// medium matches the previous fixed Tailwind grid as the default
const GRID_CONFIG = {
  small: {
    cols: { base: 4, sm: 5, md: 7, lg: 9 },
    gap: 4,
    sizes:
      '(max-width: 640px) 25vw, (max-width: 768px) 20vw, (max-width: 1024px) 14vw, 11vw',
  },
  medium: {
    cols: { base: 3, sm: 4, md: 5, lg: 6 },
    gap: 8,
    sizes:
      '(max-width: 640px) 33vw, (max-width: 768px) 25vw, (max-width: 1024px) 20vw, 16vw',
  },
  large: {
    cols: { base: 2, sm: 3, md: 4, lg: 5 },
    gap: 10,
    sizes:
      '(max-width: 640px) 50vw, (max-width: 768px) 33vw, (max-width: 1024px) 25vw, 20vw',
  },
} as const
export function ImageLibraryModal({
  isOpen,
  onClose,
  context,
  onSelect,
  onSelectMultiple,
  invitationId,
  isAuthenticated,
  initialSelectedId = null,
  initialSelectedUrls = [],
  maxImages,
  orientation,
  preferredAspectRatio,
  recommendedDimensions: recommendedDimensionsProp,
  allowedAspectRatios,
  autoOpenExport = false,
  contextLabel: contextLabelProp,
  allowDuplicates = false,
  onSelectWithInstances,
}: ImageLibraryModalProps) {
  type UploadingEntry = {
    id: string
    name: string
    preview: string
    file: File
    status: UploadLifecycleStatus
    progress: number
    error?: string
    isHeic?: boolean
  }

  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const activeUploadTaskRef = useRef<DirectUploadBatchTask | null>(null)
  /** IDs user cancelled during preparing/upload; loop skips these so we do not overwrite aborted status. */
  const cancelledUploadIdsRef = useRef<Set<string>>(new Set())

  /** deferPreview: skip object URLs so first paint is instant; fill in one per frame via schedulePreviewUrls. */
  const createUploadingEntries = useCallback(
    (
      files: File[],
      initialStatus: UploadLifecycleStatus = 'queued',
      deferPreview = false
    ): UploadingEntry[] => {
      const heicTypes = ['image/heic', 'image/heif']
      return files.map((file) => ({
        id: `uploading-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        name: file.name,
        preview: deferPreview ? '' : URL.createObjectURL(file),
        file,
        status: initialStatus,
        progress: 0,
        isHeic: heicTypes.includes(file.type?.toLowerCase() ?? ''),
      }))
    },
    []
  )

  /** Fill preview URLs one per frame so main thread can paint between each. */
  const schedulePreviewUrls = useCallback((entries: UploadingEntry[]) => {
    let i = 0
    const run = () => {
      if (i >= entries.length) return
      const entry = entries[i++]
      const url = URL.createObjectURL(entry.file)
      setUploadingFiles((prev) =>
        prev.map((e) => (e.id === entry.id ? { ...e, preview: url } : e))
      )
      requestAnimationFrame(run)
    }
    requestAnimationFrame(run)
  }, [])

  /** Revoke old preview if set, set processed file and new preview, status queued. */
  const updateEntryWithProcessedFile = useCallback(
    (fileId: string, processedFile: File) => {
      setUploadingFiles((prev) =>
        prev.map((entry) => {
          if (entry.id !== fileId) return entry
          if (entry.preview) URL.revokeObjectURL(entry.preview)
          return {
            ...entry,
            file: processedFile,
            preview: URL.createObjectURL(processedFile),
            name: processedFile.name,
            status: 'queued' as const,
          }
        })
      )
    },
    []
  )

  // selectedId: legacy single-select state
  const [selectedId, setSelectedId] = useState<string | null>(initialSelectedId)
  // bulkSelectedIds: used for delete operations (browse mode) and legacy multi-select
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<string>>(new Set())
  // pickerSelection: ordered array for duplicate-allowed picker mode
  const [pickerSelection, setPickerSelection] = useState<PickerSelection[]>([])

  // Set of unique imageIds in pickerSelection — used for visual highlighting in the grid
  const pickerSelectedIds = useMemo(
    () => new Set(pickerSelection.map((p) => p.imageId)),
    [pickerSelection]
  )

  const [editingImage, setEditingImage] = useState<StoredImage | null>(null)
  const [isSavingEdit, setIsSavingEdit] = useState(false)
  const [uploadingFiles, setUploadingFiles] = useState<UploadingEntry[]>([])
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [newImageIds, setNewImageIds] = useState<string[]>([])
  const [isContextDismissed, setIsContextDismissed] = useState(false)
  const [isConnectingDrive, setIsConnectingDrive] = useState(false)
  const [showExportDialog, setShowExportDialog] = useState(false)
  const [activeExportJobId, setActiveExportJobId] = useState<string | null>(
    null
  )
  const hasShownExportToast = useRef(false)

  // When context is dismissed the editor unlocks aspect ratio restrictions
  const effectiveContext =
    isContextDismissed || context === 'general' ? 'general' : context

  const [status, setStatus] = useState<ImageStatus>('all')
  const [sort, setSort] = useState<ImageSortOption>('recent')
  const [uploaderFilter, setUploaderFilter] = useState<string>('all')
  const [gridSize, setGridSize] = useState<'small' | 'medium' | 'large'>(
    'medium'
  )
  const [activeTab, setActiveTab] = useState<'your-uploads' | 'guest-uploads'>(
    'your-uploads'
  )
  // Inline error when file validation fails; toast can be missed if Toaster not yet mounted.
  const [uploadValidationError, setUploadValidationError] = useState<
    string | null
  >(null)

  // Both queries enabled when open so tab badge counts are always fresh
  const {
    data: userImagesData,
    isLoading: isLoadingUserImages,
    fetchNextPage: fetchNextUserImages,
    hasNextPage: hasMoreUserImages,
    isFetchingNextPage: isFetchingMoreUserImages,
  } = useUserImagesInfinite({ limit: 30, enabled: isAuthenticated && isOpen })

  const {
    data: userGuestUploadsData,
    isLoading: isLoadingGuestUploads,
    fetchNextPage: fetchNextGuestUploads,
    hasNextPage: hasMoreGuestUploads,
    isFetchingNextPage: isFetchingMoreGuestUploads,
  } = useUserGuestUploadsInfinite({
    limit: 30,
    enabled: isAuthenticated && isOpen,
  })

  const yourUploadsTotal = userImagesData?.pages[0]?.data.total ?? 0
  const guestUploadsTotal = userGuestUploadsData?.pages[0]?.data.total ?? 0

  const {
    data: guestImagesData,
    isLoading: isLoadingGuestImages,
    fetchNextPage: fetchNextGuestImages,
    hasNextPage: hasMoreGuestImages,
    isFetchingNextPage: isFetchingMoreGuestImages,
  } = useGuestImagesInfinite(invitationId ?? '', {
    limit: 30,
    enabled: !isAuthenticated && !!invitationId && isOpen,
  })

  const images = useMemo(() => {
    if (isAuthenticated) {
      if (activeTab === 'guest-uploads') {
        return (
          userGuestUploadsData?.pages.flatMap((page) => page.data.images) ?? []
        )
      }
      return userImagesData?.pages.flatMap((page) => page.data.images) ?? []
    }
    return guestImagesData?.pages.flatMap((page) => page.data.images) ?? []
  }, [
    isAuthenticated,
    activeTab,
    userImagesData,
    userGuestUploadsData,
    guestImagesData,
  ])

  const hasNextPage = isAuthenticated
    ? activeTab === 'your-uploads'
      ? hasMoreUserImages
      : hasMoreGuestUploads
    : hasMoreGuestImages
  const isFetchingNextPage = isAuthenticated
    ? activeTab === 'your-uploads'
      ? isFetchingMoreUserImages
      : isFetchingMoreGuestUploads
    : isFetchingMoreGuestImages
  const fetchNextPage = isAuthenticated
    ? activeTab === 'your-uploads'
      ? fetchNextUserImages
      : fetchNextGuestUploads
    : fetchNextGuestImages

  const deleteUserMutation = useDeleteUserImages()
  const deleteGuestMutation = useDeleteGuestImages()
  const isDeleting =
    deleteUserMutation.isPending || deleteGuestMutation.isPending
  const toggleFavoriteMutation = useToggleFavoriteImages()

  const { data: connectionsData } = useCloudConnections({
    enabled: isOpen && isAuthenticated && activeTab === 'guest-uploads',
  })
  const isGoogleConnected =
    connectionsData?.data?.connections?.some(
      (c) => c.provider === 'google-drive'
    ) ?? false
  const googleConnection = connectionsData?.data?.connections?.find(
    (c) => c.provider === 'google-drive'
  )

  const startExportMutation = useStartExportAllGuestUploads()
  const disconnectMutation = useDisconnectProvider()
  const { data: exportJobData } = useExportJob(activeExportJobId, {
    enabled: !!activeExportJobId,
    pollInterval: 1500,
  })
  const exportJob = exportJobData?.data

  const totalSizeBytes = useMemo(
    () =>
      images.reduce(
        (sum, img) => sum + (img.totalSizeBytes || img.size || 0),
        0
      ),
    [images]
  )

  const handleConnectGoogleDrive = async () => {
    setIsConnectingDrive(true)
    try {
      const result = await connectExportProviderInPopup('google-drive')
      if (result.success) {
        applyOptimisticCloudConnection(
          queryClient,
          'google-drive',
          result.email
        )
        showToast(
          'success',
          `Connected to Google Drive${result.email ? ` as ${result.email}` : ''}`
        )
        scheduleCloudConnectionsReconcile(queryClient)
      } else if (result.error !== EXPORT_OAUTH_CANCELLED) {
        showToast('error', result.error)
        await queryClient.invalidateQueries({
          queryKey: exportKeys.connections(),
        })
      }
    } catch {
      showToast('error', 'Failed to connect. Please try again.')
    } finally {
      setIsConnectingDrive(false)
    }
  }

  const handleExportAll = async () => {
    if (!isGoogleConnected) return
    try {
      hasShownExportToast.current = false
      const result = await startExportMutation.mutateAsync({
        folderName: 'DDAY - All Guest Uploads',
      })
      setActiveExportJobId(result.data.jobId)
      showToast(
        'info',
        `Starting export of ${result.data.totalImages} images...`
      )
    } catch {
      showToast('error', 'Failed to start export. Please try again.')
    }
  }

  const handleDisconnect = async () => {
    try {
      await disconnectMutation.mutateAsync('google-drive')
      showToast('success', 'Disconnected from Google Drive')
      setShowExportDialog(false)
      setActiveExportJobId(null)
    } catch {
      showToast('error', 'Failed to disconnect. Please try again.')
    }
  }

  // Show a toast once when the polled export job reaches a terminal state
  useEffect(() => {
    if (!exportJob) return
    if (exportJob.status === 'completed' && !hasShownExportToast.current) {
      hasShownExportToast.current = true
      showToast(
        'success',
        `Exported ${exportJob.progress.completed} images to Google Drive!`
      )
    } else if (exportJob.status === 'failed' && !hasShownExportToast.current) {
      hasShownExportToast.current = true
      showToast('error', exportJob.error || 'Export failed')
      setActiveExportJobId(null)
    }
  }, [exportJob])

  const newImageIdsSet = useMemo(() => new Set(newImageIds), [newImageIds])

  // hasInitializedRef prevents re-running the pre-select logic on every image page load
  const hasInitializedRef = useRef(false)
  const hasTrackedOpen = useRef(false)

  const isPickerMode = context !== 'general'
  const isBrowseMode = context === 'general'

  useEffect(() => {
    if (isOpen) {
      setSelectedId(initialSelectedId)
      setIsContextDismissed(false)
      setBulkSelectedIds(new Set())
      setPickerSelection([])
      if (autoOpenExport) {
        setActiveTab('guest-uploads')
        setShowExportDialog(true)
      } else {
        setActiveTab('your-uploads')
      }
      setUploaderFilter('all')
      hasInitializedRef.current = false

      if (!hasTrackedOpen.current) {
        hasTrackedOpen.current = true
        analytics.imageLibrary.opened(
          context,
          autoOpenExport ? 'guest-uploads' : 'your-uploads'
        )
      }
      hasUserInteracted.current = false
    } else {
      hasTrackedOpen.current = false
      hasUserInteracted.current = false
      setBulkSelectedIds(new Set())
      setPickerSelection([])
      setSelectedId(null)
    }
  }, [isOpen, initialSelectedId, autoOpenExport, context])

  const prevTabRef = useRef(activeTab)
  const hasUserInteracted = useRef(false)

  // Image IDs differ per tab so selections must be cleared on switch
  useEffect(() => {
    if (
      isOpen &&
      prevTabRef.current !== activeTab &&
      hasUserInteracted.current
    ) {
      analytics.imageLibrary.tabSwitched(prevTabRef.current, activeTab)
    }
    prevTabRef.current = activeTab
    setBulkSelectedIds(new Set())
    setPickerSelection([])
    setSelectedId(null)
  }, [activeTab, isOpen])

  // Runs once after images first load to restore previously selected URLs
  useEffect(() => {
    if (
      isOpen &&
      !hasInitializedRef.current &&
      initialSelectedUrls.length > 0 &&
      images.length > 0
    ) {
      hasInitializedRef.current = true

      if (allowDuplicates) {
        // Preserve order and duplicates from the incoming URL list
        const newPickerSelection: PickerSelection[] = []
        for (const url of initialSelectedUrls) {
          const matchedImage = images.find(
            (img) =>
              img.imageUrl === url ||
              getImageUrl(img.imageUrl, 'web') === url ||
              getImageUrl(img.imageUrl, 'preview') === url
          )
          if (matchedImage) {
            newPickerSelection.push({
              instanceId: generateInstanceId(),
              imageId: matchedImage.id,
              imageUrl: matchedImage.imageUrl,
            })
          }
        }
        if (newPickerSelection.length > 0)
          setPickerSelection(newPickerSelection)
      } else {
        const matchedIds = new Set<string>()
        for (const url of initialSelectedUrls) {
          const matchedImage = images.find(
            (img) =>
              img.imageUrl === url ||
              getImageUrl(img.imageUrl, 'web') === url ||
              getImageUrl(img.imageUrl, 'preview') === url
          )
          if (matchedImage) matchedIds.add(matchedImage.id)
        }
        if (matchedIds.size > 0) setBulkSelectedIds(matchedIds)
      }
    }
  }, [isOpen, initialSelectedUrls, images, allowDuplicates])

  const toggleMultiSelect = useCallback(
    (imageId: string, image?: StoredImage) => {
      if (isBrowseMode) {
        setBulkSelectedIds((prev) => {
          const next = new Set(prev)
          if (next.has(imageId)) {
            next.delete(imageId)
          } else {
            if (next.size >= MAX_IMAGE_SELECTION) {
              showToast('warning', 'Maximum selection limit reached')
              return prev
            }
            next.add(imageId)
          }
          return next
        })
      } else if (allowDuplicates && image) {
        // Duplicate picker: always ADD, never toggle
        setPickerSelection((prev) => {
          const effectiveMax =
            maxImages ?? CONTEXT_IMAGE_LIMITS[context]?.max ?? 1
          if (prev.length >= effectiveMax) {
            showToast('info', `Maximum of ${effectiveMax} images allowed`)
            return prev
          }
          if (prev.length >= MAX_IMAGE_SELECTION) {
            showToast('warning', 'Maximum selection limit reached')
            return prev
          }
          return [
            ...prev,
            {
              instanceId: generateInstanceId(),
              imageId: image.id,
              imageUrl: image.imageUrl,
            },
          ]
        })
      } else {
        // Standard picker: toggle behavior
        setBulkSelectedIds((prev) => {
          const next = new Set(prev)
          if (next.has(imageId)) {
            next.delete(imageId)
          } else {
            const effectiveMax =
              maxImages ?? CONTEXT_IMAGE_LIMITS[context]?.max ?? 1
            if (next.size >= effectiveMax) {
              showToast('info', `Maximum of ${effectiveMax} images allowed`)
              return prev
            }
            if (next.size >= MAX_IMAGE_SELECTION) {
              showToast('warning', 'Maximum selection limit reached')
              return prev
            }
            next.add(imageId)
          }
          return next
        })
      }
    },
    [isBrowseMode, allowDuplicates, maxImages, context]
  )

  const clearMultiSelect = useCallback(() => {
    setBulkSelectedIds(new Set())
    setPickerSelection([])
  }, [])

  const removeFromPickerSelection = useCallback((instanceId: string) => {
    setPickerSelection((prev) =>
      prev.filter((sel) => sel.instanceId !== instanceId)
    )
  }, [])

  // imageId → count map for showing ×N badges on cards in duplicate mode
  const pickerSelectionCountMap = useMemo(() => {
    const map = new Map<string, number>()
    pickerSelection.forEach((sel) => {
      map.set(sel.imageId, (map.get(sel.imageId) || 0) + 1)
    })
    return map
  }, [pickerSelection])

  const effectiveSelectionCount = allowDuplicates
    ? pickerSelection.length
    : bulkSelectedIds.size

  const handleToggleFavorite = useCallback(
    (imageId: string) => {
      if (!isAuthenticated) return
      const image = images.find((img) => img.id === imageId)
      if (!image) return
      toggleFavoriteMutation.mutate({
        imageIds: [imageId],
        favorite: !image.isFavorite,
      })
    },
    [isAuthenticated, images, toggleFavoriteMutation]
  )

  const handleBulkDelete = useCallback(async () => {
    if (bulkSelectedIds.size === 0) return
    if (!isAuthenticated && !invitationId) return

    const idsToDelete = Array.from(bulkSelectedIds)

    try {
      if (isAuthenticated) {
        await deleteUserMutation.mutateAsync({ imageIds: idsToDelete })
      } else {
        await deleteGuestMutation.mutateAsync({
          invitationId: invitationId!,
          imageIds: idsToDelete,
        })
      }

      setBulkSelectedIds(new Set())
      setPickerSelection((prev) =>
        prev.filter((sel) => !idsToDelete.includes(sel.imageId))
      )
      if (selectedId && bulkSelectedIds.has(selectedId)) setSelectedId(null)
    } catch (error) {
      logger.error('Failed to delete images:', error)
      showToast('error', 'Failed to delete images. Please try again.')
    }
  }, [
    bulkSelectedIds,
    selectedId,
    invitationId,
    isAuthenticated,
    deleteUserMutation,
    deleteGuestMutation,
  ])

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const updateUploadingState = useCallback(
    (
      fileId: string,
      patch: Partial<{
        status: UploadLifecycleStatus
        progress: number
        error?: string
      }>
    ) => {
      setUploadingFiles((prev) =>
        prev.map((entry) =>
          entry.id === fileId ? { ...entry, ...patch } : entry
        )
      )
    },
    []
  )

  const removeUploadingEntries = useCallback((ids: string[]) => {
    if (!ids.length) return
    setUploadingFiles((prev) => {
      const removeSet = new Set(ids)
      prev
        .filter((entry) => removeSet.has(entry.id))
        .forEach((entry) => {
          if (entry.preview) URL.revokeObjectURL(entry.preview)
        })
      return prev.filter((entry) => !removeSet.has(entry.id))
    })
  }, [])

  const removeSuccessfulUploadingEntries = useCallback((ids: string[]) => {
    if (!ids.length) return
    setUploadingFiles((prev) => {
      const targetIds = new Set(ids)
      const next: typeof prev = []
      for (const entry of prev) {
        const shouldRemove =
          targetIds.has(entry.id) && entry.status === 'success'
        if (shouldRemove) {
          if (entry.preview) URL.revokeObjectURL(entry.preview)
          continue
        }
        next.push(entry)
      }
      return next
    })
  }, [])

  const runUploadBatch = useCallback(
    async (
      entries: Array<{ id: string; file: File }>,
      options?: { skipToast?: boolean }
    ): Promise<DirectUploadBatchResult | undefined> => {
      if (entries.length === 0) return undefined

      if (!isAuthenticated && !invitationId) {
        showToast(
          'warning',
          'Please save your invitation first before uploading images.'
        )
        return undefined
      }

      const task = startDirectUploadBatch({
        files: entries.map((entry) => entry.file),
        fileIds: entries.map((entry) => entry.id),
        invitationId: isAuthenticated ? undefined : invitationId!,
        authState: isAuthenticated ? 'auth' : 'unauth',
        expectedImageType: 'og',
        maxBytes: MAX_FILE_SIZE,
        concurrency: getDirectUploadHostConcurrency(),
        onFileStateChange: (state) => {
          updateUploadingState(state.fileId, {
            status: state.status,
            progress: state.progress,
            error: state.error,
          })
        },
      })

      activeUploadTaskRef.current = task

      let result: DirectUploadBatchResult
      try {
        result = await task.promise
      } finally {
        activeUploadTaskRef.current = null
      }

      if (result.images.length > 0) {
        setNewImageIds((prev) => [
          ...result.images.map((img) => img.id),
          ...prev,
        ])
        await queryClient.invalidateQueries({
          queryKey: isAuthenticated
            ? libraryKeys.user()
            : libraryKeys.guest(invitationId!),
        })
      }

      removeSuccessfulUploadingEntries(entries.map((entry) => entry.id))

      if (result.images.length === 0 && result.failed.length === 0)
        return result

      const skipToast = options?.skipToast === true
      if (!skipToast) {
        if (result.failed.length === 0) {
          showToast('success', `${result.images.length} image(s) uploaded`)
        } else if (result.images.length === 0) {
          showToast(
            'error',
            `Upload failed: ${formatFailedForDisplay(result.failed)}`
          )
        } else {
          showToast(
            'warning',
            `${result.images.length} uploaded, ${result.failed.length} failed: ${formatFailedForDisplay(result.failed)}`
          )
        }
      }
      return result
    },
    [
      invitationId,
      isAuthenticated,
      queryClient,
      removeSuccessfulUploadingEntries,
      updateUploadingState,
    ]
  )

  const handleFileInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files
      if (!fileList || fileList.length === 0) return

      const files = Array.from(fileList)
      e.target.value = ''
      setUploadValidationError(null)

      if (!isAuthenticated && !invitationId) {
        logger.error('Cannot upload: invitationId is required for guests')
        const msg = 'Please save your invitation first before uploading images.'
        setUploadValidationError(msg)
        showToast('warning', msg)
        return
      }

      const validation = validateMultipleImageFiles(
        files,
        MAX_FILES,
        MAX_FILE_SIZE
      )
      if (!validation.valid) {
        const msg = validation.error ?? 'Please check your files and try again.'
        setUploadValidationError(msg)
        showToast('warning', msg)
        return
      }

      cancelledUploadIdsRef.current.clear()

      // Defer preview URLs so first paint is instant; fill one per frame.
      const baseEntries = createUploadingEntries(files, 'initializing', true)
      setUploadingFiles((prev) => [...baseEntries, ...prev])
      schedulePreviewUrls(baseEntries)

      showToast(
        'info',
        `Preparing ${files.length} image${files.length !== 1 ? 's' : ''}…`
      )

      const processingFailed: Array<{ filename: string; error: string }> = []
      const uploadResults: DirectUploadBatchResult[] = []

      const yieldToMain = (): Promise<void> =>
        new Promise((r) => setTimeout(r, 0))
      const yieldToPaint = (): Promise<void> =>
        new Promise((r) => requestAnimationFrame(() => r()))

      try {
        for (let i = 0; i < files.length; i += 1) {
          await yieldToMain()
          await yieldToPaint()
          const file = files[i]
          const entry = baseEntries[i]
          if (!entry) continue
          if (cancelledUploadIdsRef.current.has(entry.id)) continue

          try {
            const processedFile = await processFileForUpload(file)
            if (cancelledUploadIdsRef.current.has(entry.id)) continue
            updateEntryWithProcessedFile(entry.id, processedFile)
            const result = await runUploadBatch(
              [{ id: entry.id, file: processedFile }],
              { skipToast: true }
            )
            if (result) uploadResults.push(result)
          } catch (err) {
            logger.error(`Failed to process or upload ${file.name}:`, err)
            const message = err instanceof Error ? err.message : 'Unknown error'
            processingFailed.push({ filename: file.name, error: message })
            updateUploadingState(entry.id, {
              status: 'failed',
              progress: 0,
              error: message,
            })
          }
        }

        if (processingFailed.length > 0) {
          showToast(
            'warning',
            `${processingFailed.length} image(s) could not be processed: ${formatFailedForDisplay(processingFailed)}. Try converting to JPEG first.`
          )
        }

        const totalImages = uploadResults.reduce(
          (sum, r) => sum + r.images.length,
          0
        )
        const totalFailed = uploadResults.reduce(
          (sum, r) => sum + r.failed.length,
          0
        )

        if (totalImages > 0 || totalFailed > 0) {
          if (totalFailed === 0) {
            showToast('success', `${totalImages} image(s) uploaded`)
          } else if (totalImages === 0) {
            showToast(
              'error',
              `Upload failed${processingFailed.length > 0 ? ` (${processingFailed.length} processing errors)` : ''}. Please try again.`
            )
          } else {
            showToast(
              'warning',
              `${totalImages} uploaded, ${totalFailed} failed${processingFailed.length > 0 ? `; ${processingFailed.length} could not be processed` : ''}`
            )
          }
        } else if (processingFailed.length === 0) {
          showToast('warning', 'No valid images to upload.')
        }
      } catch (err) {
        logger.error('Upload failed:', err)
        const failed = getFailedFromApiError(err)
        if (failed?.length) {
          showToast('error', `Upload failed: ${formatFailedForDisplay(failed)}`)
        } else {
          showToast(
            'error',
            err instanceof ApiError
              ? err.message
              : 'Image upload failed. Please try again.'
          )
        }
      }
    },
    [
      createUploadingEntries,
      invitationId,
      isAuthenticated,
      runUploadBatch,
      schedulePreviewUrls,
      updateEntryWithProcessedFile,
      updateUploadingState,
    ]
  )

  useEffect(() => {
    if (!isOpen) setUploadValidationError(null)
    else preloadHeicWorker()
  }, [isOpen])

  const handleCancelUpload = useCallback(
    (fileId: string) => {
      cancelledUploadIdsRef.current.add(fileId)
      activeUploadTaskRef.current?.cancelFile(fileId)
      updateUploadingState(fileId, { status: 'aborted', progress: 0 })
    },
    [updateUploadingState]
  )

  const handleRetryUpload = useCallback(
    async (fileId: string) => {
      const entry = uploadingFiles.find((item) => item.id === fileId)
      // Allow retry for both failed and aborted (canceled) uploads
      if (!entry || (entry.status !== 'failed' && entry.status !== 'aborted'))
        return
      updateUploadingState(fileId, {
        status: 'queued',
        progress: 0,
        error: undefined,
      })
      await runUploadBatch([{ id: entry.id, file: entry.file }])
    },
    [runUploadBatch, updateUploadingState, uploadingFiles]
  )

  const handleRemoveUploadingFile = useCallback(
    (fileId: string) => {
      removeUploadingEntries([fileId])
    },
    [removeUploadingEntries]
  )

  useEffect(() => {
    return () => {
      activeUploadTaskRef.current?.cancelAll()
      terminateHeicWorker()
      setUploadingFiles((prev) => {
        prev.forEach((entry) => {
          if (entry.preview) URL.revokeObjectURL(entry.preview)
        })
        return []
      })
    }
  }, [])

  const handleEditedImageUpload = useCallback(
    async (file: File): Promise<StoredImage> => {
      if (!isAuthenticated && !invitationId) {
        throw new Error('invitationId is required for guest uploads')
      }

      const response = isAuthenticated
        ? await uploadUserImages([file], 'edit')
        : await uploadGuestImages([file], invitationId!, 'edit')

      if (response.images.length === 0) {
        throw new Error('Upload failed - no image returned')
      }

      const editedImage = response.images[0]
      setNewImageIds((prev) => [editedImage.id, ...prev])
      await queryClient.invalidateQueries({
        queryKey: isAuthenticated
          ? libraryKeys.user()
          : libraryKeys.guest(invitationId!),
      })
      return editedImage
    },
    [invitationId, isAuthenticated, queryClient]
  )

  const counts = useMemo(
    () => ({
      all: images.length,
      original: images.filter((img) => !img.isEdited).length,
      edited: images.filter((img) => img.isEdited).length,
      favorites: images.filter((img) => img.isFavorite).length,
    }),
    [images]
  )

  const uniqueUploaders = useMemo(() => {
    if (activeTab !== 'guest-uploads') return []
    return [
      ...new Set(images.map((img) => img.uploadedBy).filter(Boolean)),
    ].sort((a, b) => (a || '').localeCompare(b || '')) as string[]
  }, [images, activeTab])

  const guestUploaderDisplayById = useMemo(() => {
    if (activeTab !== 'guest-uploads') return new Map<string, string>()
    return buildMergedUploaderDisplayMap(uniqueUploaders, images)
  }, [activeTab, uniqueUploaders, images])

  const filteredImages = useMemo(() => {
    let result = [...images]

    if (activeTab === 'guest-uploads' && uploaderFilter !== 'all') {
      result = result.filter((img) => img.uploadedBy === uploaderFilter)
    }

    if (status === 'original') result = result.filter((img) => !img.isEdited)
    else if (status === 'edited') result = result.filter((img) => img.isEdited)
    else if (status === 'favorites')
      result = result.filter((img) => img.isFavorite)

    result.sort((a, b) => {
      switch (sort) {
        case 'recent':
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
        case 'oldest':
          return (
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          )
        case 'name':
          return a.filename.localeCompare(b.filename)
        default:
          return 0
      }
    })

    // Newly uploaded images (this session) stay at the front so they "fill in"
    // the slot where the uploading card was, without changing overall sort order.
    if (newImageIds.length === 0) return result
    const newIdsSet = new Set(newImageIds)
    const atFront = result.filter((img) => newIdsSet.has(img.id))
    const rest = result.filter((img) => !newIdsSet.has(img.id))
    atFront.sort(
      (a, b) => newImageIds.indexOf(a.id) - newImageIds.indexOf(b.id)
    )
    return [...atFront, ...rest]
  }, [images, status, sort, activeTab, uploaderFilter, newImageIds])

  const handleImageSelect = useCallback((image: StoredImage) => {
    setSelectedId(image.id)
  }, [])

  const handleEdit = useCallback((image: StoredImage) => {
    setEditingImage(image)
  }, [])

  const handleEditorSave = useCallback(
    async (editedImage: StoredImage, file: File) => {
      if (!isAuthenticated && !invitationId) {
        setEditingImage(null)
        setSelectedId(editedImage.id)
        return
      }

      setIsSavingEdit(true)
      try {
        const uploadedImage = await handleEditedImageUpload(file)
        setEditingImage(null)
        setSelectedId(uploadedImage.id)
      } catch (error) {
        logger.error('Failed to upload edited image:', error)
        showToast('error', 'Failed to save edited image. Please try again.')
      } finally {
        setIsSavingEdit(false)
      }
    },
    [isAuthenticated, invitationId, handleEditedImageUpload]
  )

  const handleEditorCancel = useCallback(() => {
    setEditingImage(null)
  }, [])

  // Stable callback prevents re-render loops from Dialog's onOpenChange
  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      if (!open) onClose()
    },
    [onClose]
  )

  const {
    contextLabel,
    preferredRatio,
    recommendedDimensions,
    imageLimits,
    isMultiSelectMode,
  } = useMemo(() => {
    const contextLimits = CONTEXT_IMAGE_LIMITS[context]
    const limits = maxImages
      ? { min: contextLimits.min, max: maxImages }
      : contextLimits

    return {
      contextLabel: contextLabelProp || CONTEXT_LABELS[context],
      preferredRatio: preferredAspectRatio || CONTEXT_ASPECT_RATIOS[context],
      recommendedDimensions:
        recommendedDimensionsProp || CONTEXT_DIMENSIONS[context],
      imageLimits: limits,
      isMultiSelectMode: limits.max > 1,
    }
  }, [
    context,
    preferredAspectRatio,
    recommendedDimensionsProp,
    maxImages,
    contextLabelProp,
  ])

  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1024
  )
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const activeColumns = useMemo(() => {
    const cfg = GRID_CONFIG[gridSize].cols
    if (windowWidth >= 1024) return cfg.lg
    if (windowWidth >= 768) return cfg.md
    if (windowWidth >= 640) return cfg.sm
    return cfg.base
  }, [gridSize, windowWidth])

  const GRID_ICONS = {
    small: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        {([0, 5, 10, 15] as const).flatMap((x) =>
          ([0, 5, 10, 15] as const).map((y) => (
            <rect key={`${x}-${y}`} x={x} y={y} width="3" height="3" rx="0.4" />
          ))
        )}
      </svg>
    ),
    medium: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        {([0, 7, 14] as const).flatMap((x) =>
          ([0, 7, 14] as const).map((y) => (
            <rect key={`${x}-${y}`} x={x} y={y} width="5" height="5" rx="0.6" />
          ))
        )}
      </svg>
    ),
    large: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        {([0, 11] as const).flatMap((x) =>
          ([0, 11] as const).map((y) => (
            <rect key={`${x}-${y}`} x={x} y={y} width="8" height="8" rx="1" />
          ))
        )}
      </svg>
    ),
  }
  // ─────────────────────────────────────────────────────────────────────────

  if (!isOpen) return null

  return (
    <>
      <Dialog
        open={isOpen && !editingImage}
        onOpenChange={handleDialogOpenChange}
      >
        <DialogContent className="!w-[98vw] !max-w-[1600px] !h-[90vh] !max-h-[900px] !inset-auto !left-[50%] !top-[50%] !-translate-x-1/2 !-translate-y-1/2 flex flex-col !p-0 !gap-0 !rounded-xl">
          <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 border-b">
            <div className="flex flex-col gap-4">
              <DialogTitle className="text-lg sm:text-xl">
                My Library
                {!isContextDismissed &&
                  context !== 'general' &&
                  contextLabel && (
                    <span className="text-muted-foreground font-normal">
                      {' '}
                      — {contextLabel}
                    </span>
                  )}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Browse, filter, and manage uploaded images.
              </DialogDescription>

              {/* Slim recommendation hint — only shown in picker context */}
              {!isContextDismissed && context !== 'general' && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 bg-amber-50/80 dark:bg-amber-950/20 border border-amber-200/50 dark:border-amber-800/30 rounded-lg">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400 shrink-0">
                    Recommended
                  </span>
                  <div className="flex items-center gap-x-2 flex-wrap gap-y-1">
                    {orientation && (
                      <span className="text-xs text-foreground/80">
                        {formatOrientationLabel(orientation)}
                      </span>
                    )}
                    {orientation && <span className="text-border">·</span>}
                    <span className="text-xs text-foreground/80">
                      {preferredRatio === 'free' ? 'Any ratio' : preferredRatio}
                    </span>
                    <span className="text-border">·</span>
                    <span className="text-xs text-foreground/80">
                      {recommendedDimensions}
                    </span>
                    <span className="text-border">·</span>
                    <span className="text-xs text-foreground/80">
                      {imageLimits.max === 1
                        ? '1 image'
                        : `1–${imageLimits.max} images`}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </DialogHeader>

          {isAuthenticated && (
            <div className="px-4 sm:px-6 py-2.5 border-b">
              <div className="flex items-center gap-1.5">
                <Tooltip
                  content="Photos you've uploaded to your library"
                  side="bottom"
                >
                  <button
                    onClick={() => {
                      hasUserInteracted.current = true
                      setActiveTab('your-uploads')
                      setUploaderFilter('all')
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 border touch-manipulation ${
                      activeTab === 'your-uploads'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
                    }`}
                  >
                    Your Uploads
                    <span
                      className={`ml-1.5 text-xs ${activeTab === 'your-uploads' ? 'opacity-80' : 'opacity-60'}`}
                    >
                      ({yourUploadsTotal})
                    </span>
                  </button>
                </Tooltip>
                <Tooltip
                  content="Photos uploaded by guests to your invitations"
                  side="bottom"
                >
                  <button
                    onClick={() => {
                      hasUserInteracted.current = true
                      setActiveTab('guest-uploads')
                      setUploaderFilter('all')
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 border touch-manipulation ${
                      activeTab === 'guest-uploads'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
                    }`}
                  >
                    Guest Uploads
                    <span
                      className={`ml-1.5 text-xs ${activeTab === 'guest-uploads' ? 'opacity-80' : 'opacity-60'}`}
                    >
                      ({guestUploadsTotal})
                    </span>
                  </button>
                </Tooltip>
              </div>
            </div>
          )}

          <div className="px-4 sm:px-6 py-3 border-b">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
              <div className="flex-1 order-last sm:order-0">
                <FilterPills
                  status={status}
                  onStatusChange={setStatus}
                  sort={sort}
                  onSortChange={setSort}
                  counts={counts}
                  hideSort
                  showFavorites={
                    isAuthenticated && activeTab === 'your-uploads'
                  }
                />
              </div>

              {activeTab === 'guest-uploads' &&
                isAuthenticated &&
                images.length > 0 && (
                  <div className="flex items-center order-1 sm:order-0 w-full sm:w-auto">
                    {isGoogleConnected ? (
                      <Tooltip content="Export all guest uploads to your Google Drive">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowExportDialog(true)}
                          className="text-xs w-full sm:w-auto"
                        >
                          <svg
                            className="w-3.5 h-3.5 mr-1.5"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z" />
                          </svg>
                          Export All
                        </Button>
                      </Tooltip>
                    ) : (
                      <Tooltip content="Connect your Google Drive to export guest uploads">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleConnectGoogleDrive}
                          disabled={isConnectingDrive}
                          className="text-xs w-full sm:w-auto"
                        >
                          {isConnectingDrive ? (
                            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                          ) : (
                            <svg
                              className="w-3.5 h-3.5 mr-1.5"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                            >
                              <path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z" />
                            </svg>
                          )}
                          Link Google Drive
                        </Button>
                      </Tooltip>
                    )}
                  </div>
                )}

              {activeTab === 'guest-uploads' &&
                isAuthenticated &&
                images.length > 0 &&
                uniqueUploaders.length > 0 && (
                  <div className="hidden sm:block w-px h-6 bg-border" />
                )}

              {activeTab === 'guest-uploads' && uniqueUploaders.length > 0 && (
                <div className="flex items-center gap-2 order-2 sm:order-0 w-full sm:w-auto">
                  <Tooltip content="Select a specific guest's uploads">
                    <span className="text-sm text-muted-foreground shrink-0 cursor-help">
                      Uploaded By:
                    </span>
                  </Tooltip>
                  <Select
                    value={uploaderFilter}
                    onValueChange={setUploaderFilter}
                  >
                    <SelectTrigger className="flex-1 sm:w-[140px] sm:max-w-[140px] h-8 text-sm">
                      <SelectValue
                        placeholder="All Guests"
                        className="truncate"
                      />
                    </SelectTrigger>
                    <SelectContent className="max-w-[220px]">
                      <SelectItem value="all">
                        All Guests ({images.length})
                      </SelectItem>
                      {uniqueUploaders.map((id) => {
                        const count = images.filter(
                          (img) => img.uploadedBy === id
                        ).length
                        const label = guestUploaderDisplayById.get(id) ?? id
                        return (
                          <SelectItem key={id} value={id} className="truncate">
                            <span className="truncate max-w-[150px] inline-block align-bottom">
                              {label}
                            </span>{' '}
                            ({count})
                          </SelectItem>
                        )
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex items-center gap-2 order-2 sm:order-0">
                <span className="text-sm text-muted-foreground">Sort:</span>
                <Select
                  value={sort}
                  onValueChange={(value) => setSort(value as ImageSortOption)}
                >
                  <SelectTrigger className="flex-1 sm:w-[100px] h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recent">Recent</SelectItem>
                    <SelectItem value="oldest">Oldest</SelectItem>
                    <SelectItem value="name">Name</SelectItem>
                  </SelectContent>
                </Select>

                <div className="w-px h-4 bg-border shrink-0" />

                <div className="shrink-0 flex items-center gap-0.5 rounded-md border border-border bg-muted/40 p-0.5">
                  {(['small', 'medium', 'large'] as const).map((size) => (
                    <button
                      key={size}
                      onClick={() => setGridSize(size)}
                      aria-label={`${size} grid`}
                      aria-pressed={gridSize === size}
                      className={`p-1.5 rounded transition-all duration-150 cursor-pointer touch-manipulation ${
                        gridSize === size
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {GRID_ICONS[size]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* min-h-0 so this flex child can shrink and scroll during uploads */}
          <div
            className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4 touch-pan-y overscroll-y-contain"
            onScroll={(e) => {
              const target = e.target as HTMLDivElement
              const scrolledToBottom =
                target.scrollHeight - target.scrollTop <=
                target.clientHeight + 200
              if (scrolledToBottom && hasNextPage && !isFetchingNextPage) {
                fetchNextPage()
              }
            }}
          >
            {isAuthenticated &&
            (isLoadingUserImages || isLoadingGuestUploads) ? (
              <div className="flex items-center justify-center py-16">
                <Spinner size="lg" />
              </div>
            ) : !isAuthenticated && isLoadingGuestImages ? (
              <div className="flex items-center justify-center py-16">
                <Spinner size="lg" />
              </div>
            ) : activeTab === 'guest-uploads' && filteredImages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 mb-4 rounded-full bg-muted flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-muted-foreground"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-sm font-medium text-foreground mb-1">
                  No guest uploads yet
                </h3>
                <p className="text-xs text-muted-foreground max-w-xs">
                  When guests upload photos to your live invitations,
                  they&apos;ll appear here for you to use.
                </p>
              </div>
            ) : (
              <>
                {uploadValidationError && (
                  <div
                    role="alert"
                    data-testid="library-upload-validation-error"
                    className="mx-4 mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-600 dark:bg-amber-950/40 dark:text-amber-200"
                  >
                    {uploadValidationError}
                  </div>
                )}
                <ImageGrid
                  images={filteredImages}
                  selectedId={selectedId}
                  selectedIds={
                    allowDuplicates ? pickerSelectedIds : bulkSelectedIds
                  }
                  selectionCountMap={
                    allowDuplicates ? pickerSelectionCountMap : undefined
                  }
                  onSelect={handleImageSelect}
                  onMultiSelect={toggleMultiSelect}
                  uploadingFiles={uploadingFiles}
                  onCancelUpload={handleCancelUpload}
                  onRetryUpload={handleRetryUpload}
                  onRemoveUpload={handleRemoveUploadingFile}
                  onEdit={handleEdit}
                  onToggleFavorite={
                    isAuthenticated && activeTab === 'your-uploads'
                      ? handleToggleFavorite
                      : undefined
                  }
                  newImageIds={newImageIdsSet}
                  onUploadClick={
                    activeTab === 'guest-uploads'
                      ? undefined
                      : isAuthenticated || invitationId
                        ? handleUploadClick
                        : undefined
                  }
                  columns={activeColumns}
                  gap={GRID_CONFIG[gridSize].gap}
                  imageSizes={GRID_CONFIG[gridSize].sizes}
                />

                {isFetchingNextPage && (
                  <div className="flex items-center justify-center py-4">
                    <Spinner size="sm" />
                    <span className="ml-2 text-sm text-muted-foreground">
                      Loading more...
                    </span>
                  </div>
                )}

                {!hasNextPage && filteredImages.length > 0 && (
                  <div className="text-center py-4 text-xs text-muted-foreground">
                    {filteredImages.length} image
                    {filteredImages.length !== 1 ? 's' : ''} total
                  </div>
                )}
              </>
            )}
          </div>

          {isPickerMode && allowDuplicates && (
            <SelectionTray
              selections={pickerSelection}
              maxSelections={imageLimits.max}
              onRemove={removeFromPickerSelection}
            />
          )}

          <DialogFooter className="px-4 sm:px-6 py-4 border-t bg-card">
            <div className="flex items-center justify-between w-full gap-3">
              <div className="flex items-center gap-2">
                {(isAuthenticated || invitationId) && isBrowseMode && (
                  <Button
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={isDeleting || bulkSelectedIds.size === 0}
                    variant="outline"
                    size="sm"
                    className="gap-1.5 border-rose-300 text-rose-600 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-400 disabled:border-border disabled:text-muted-foreground disabled:hover:bg-transparent"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </Button>
                )}
              </div>

              {/* Selection counter — hidden when nothing is selected */}
              {!allowDuplicates &&
                (() => {
                  const currentCount = effectiveSelectionCount
                  if (currentCount === 0) return null

                  const inContext = !isContextDismissed && context !== 'general'
                  const isOverLimit =
                    inContext && currentCount > imageLimits.max
                  const isComplete =
                    inContext && currentCount === imageLimits.max

                  return (
                    <div
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-md border transition-colors ${
                        isOverLimit
                          ? 'bg-red-50 dark:bg-red-950/30 border-red-300 dark:border-red-800'
                          : isComplete
                            ? 'bg-green-50 dark:bg-green-950/30 border-green-300 dark:border-green-800'
                            : 'bg-muted border-border'
                      }`}
                    >
                      <span
                        className={`text-sm ${
                          isOverLimit
                            ? 'text-red-600 dark:text-red-400 font-medium'
                            : isComplete
                              ? 'text-green-600 dark:text-green-400 font-medium'
                              : 'text-foreground font-medium'
                        }`}
                      >
                        {inContext
                          ? `${currentCount}/${imageLimits.max} selected`
                          : `${currentCount} selected`}
                      </span>
                      <button
                        onClick={clearMultiSelect}
                        className="p-0.5 rounded hover:bg-background text-muted-foreground hover:text-foreground transition-colors"
                        title="Clear selection"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )
                })()}

              <div className="flex items-center gap-2">
                {allowDuplicates && effectiveSelectionCount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearMultiSelect}
                    className="text-muted-foreground"
                  >
                    Clear all
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onClose}
                  className="hidden sm:inline-flex"
                >
                  Close
                </Button>
                {!isContextDismissed && context !== 'general' && (
                  <Button
                    size="sm"
                    onClick={() => {
                      if (allowDuplicates) {
                        // Duplicate mode: onSelectWithInstances preserves instanceIds for ordered/duplicate selections
                        if (onSelectWithInstances) {
                          onSelectWithInstances(pickerSelection)
                        } else if (onSelectMultiple) {
                          onSelectMultiple(
                            pickerSelection.map((sel) => ({
                              id: sel.imageId,
                              imageUrl: sel.imageUrl,
                              isEdited:
                                images.find((img) => img.id === sel.imageId)
                                  ?.isEdited || false,
                            }))
                          )
                        }
                      } else {
                        const selectedImagesArray = Array.from(bulkSelectedIds)
                          .map((id) => images.find((img) => img.id === id))
                          .filter((img): img is StoredImage => !!img)
                          .map((img) => ({
                            id: img.id,
                            imageUrl: img.imageUrl,
                            isEdited: img.isEdited || false,
                          }))

                        if (isMultiSelectMode && onSelectMultiple) {
                          onSelectMultiple(selectedImagesArray)
                        } else if (selectedImagesArray.length === 1) {
                          onSelect(selectedImagesArray[0])
                        }
                      }
                      onClose()
                    }}
                    disabled={
                      isMultiSelectMode
                        ? effectiveSelectionCount === 0 ||
                          effectiveSelectionCount > imageLimits.max
                        : effectiveSelectionCount !== 1
                    }
                  >
                    Use Selected
                  </Button>
                )}
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        multiple
        onChange={handleFileInputChange}
        className="hidden"
        aria-hidden="true"
      />

      {/* Rendered outside the library Dialog so it can go full-screen independently.
          Uses effectiveContext so aspect ratio is unlocked when context is dismissed. */}
      {editingImage && (
        <ImageEditorModal
          isOpen={!!editingImage}
          onClose={handleEditorCancel}
          image={editingImage}
          context={effectiveContext}
          onSave={handleEditorSave}
          isSaving={isSavingEdit}
          lockedAspectRatio={
            !isContextDismissed && preferredAspectRatio && !allowedAspectRatios
              ? (preferredAspectRatio as AspectRatio)
              : undefined
          }
          allowedAspectRatios={
            !isContextDismissed ? allowedAspectRatios : undefined
          }
          contextLabel={!isContextDismissed ? contextLabel : undefined}
        />
      )}

      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          await handleBulkDelete()
          setShowDeleteConfirm(false)
        }}
        title="Delete Images"
        description={`Are you sure you want to delete ${bulkSelectedIds.size} image${bulkSelectedIds.size !== 1 ? 's' : ''}? This action cannot be undone.`}
        confirmText={`Delete ${bulkSelectedIds.size} Image${bulkSelectedIds.size !== 1 ? 's' : ''}`}
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />

      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <svg
                className="w-5 h-5 text-primary"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z" />
              </svg>
              Export to Google Drive
            </DialogTitle>
            <DialogDescription className="sr-only">
              Export images to a connected Google Drive account.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg mb-4">
              <div className="flex-1 text-center">
                <p className="text-2xl font-bold text-foreground">
                  {images.length}
                </p>
                <p className="text-xs text-muted-foreground">Images</p>
              </div>
              {totalSizeBytes > 0 && (
                <>
                  <div className="w-px h-10 bg-border" />
                  <div className="flex-1 text-center">
                    <p className="text-2xl font-bold text-foreground">
                      {formatFileSize(totalSizeBytes)}
                    </p>
                    <p className="text-xs text-muted-foreground">Total Size</p>
                  </div>
                </>
              )}
            </div>

            {activeExportJobId && exportJob ? (
              <div className="py-6">
                {exportJob.status === 'completed' ? (
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      Export Complete!
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {exportJob.progress.completed} images exported
                      successfully
                      {exportJob.progress.failed > 0 && (
                        <span className="text-orange-500">
                          {' '}
                          ({exportJob.progress.failed} failed)
                        </span>
                      )}
                    </p>
                    {exportJob.folderUrl && (
                      <Button
                        onClick={() =>
                          window.open(
                            exportJob.folderUrl!,
                            '_blank',
                            'noopener,noreferrer'
                          )
                        }
                        className="gap-2"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Open in Google Drive
                      </Button>
                    )}
                  </div>
                ) : exportJob.status === 'failed' ? (
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                      <X className="w-8 h-8 text-red-600 dark:text-red-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      Export Failed
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {exportJob.error || 'An unexpected error occurred'}
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => setActiveExportJobId(null)}
                    >
                      Try Again
                    </Button>
                  </div>
                ) : (
                  <div className="text-center">
                    <Spinner size="lg" className="mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      {exportJob.status === 'queued'
                        ? 'Queued...'
                        : 'Uploading...'}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {exportJob.progress.completed} of{' '}
                      {exportJob.progress.total} images
                    </p>
                    <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all duration-300"
                        style={{ width: `${exportJob.progress.percent}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {exportJob.progress.percent}% complete
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="relative p-4 rounded-lg border-2 border-primary bg-primary/5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                        style={{ backgroundColor: '#4285F4' }}
                      >
                        G
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground">
                          Google Drive
                        </h4>
                        <p className="text-xs text-muted-foreground">
                          {googleConnection?.email}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-primary" />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs text-muted-foreground hover:text-destructive"
                        disabled={disconnectMutation.isPending}
                        onClick={handleDisconnect}
                      >
                        {disconnectMutation.isPending ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          'Disconnect'
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button
              variant="outline"
              onClick={() => setShowExportDialog(false)}
            >
              Cancel
            </Button>
            {(!activeExportJobId ||
              exportJob?.status === 'completed' ||
              exportJob?.status === 'failed') && (
              <Button
                onClick={handleExportAll}
                disabled={startExportMutation.isPending}
                className="gap-2"
              >
                {startExportMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Starting Export...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z" />
                    </svg>
                    Export {images.length} Images
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
